import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from "react";
import { encryptedPost } from "../utils/secureApi";
import { fetchBootMasterData } from "../Services/bootService";

const CategoryCacheContext = createContext(null);

const CACHE_KEY = "categoryCache";
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

// ==================== localStorage helpers ====================

function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    // Treat an EMPTY array as "no valid cache" — not merely truthy. An empty
    // categories payload (from a transient boot blip) must never count as a
    // loaded cache, or every consumer (e.g. the Inventory "Product Category"
    // filter) renders "No matches" and never recovers for the whole TTL.
    if (
      Array.isArray(cached?.data) &&
      cached.data.length > 0 &&
      cached?.timestamp &&
      Date.now() - cached.timestamp < CACHE_TTL
    ) {
      return cached.data;
    }
    return null; // expired, empty, or malformed
  } catch {
    return null;
  }
}

function writeCache(data) {
  // Never persist an empty/invalid array — caching [] would poison the cache
  // for the whole TTL and suppress the /categories/list fallback.
  if (!Array.isArray(data) || data.length === 0) return;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

function clearCache() {
  try {
    localStorage.removeItem(CACHE_KEY);
  } catch { /* ignore */ }
}

// ==================== Build flat categoryMap from tree ====================

function buildCategoryMap(tree) {
  const map = {};
  const walk = (nodes) => {
    if (!nodes) return;
    nodes.forEach((cat) => {
      map[cat.categoryId] = cat;
      map[String(cat.categoryId)] = cat;
      if (cat.children?.length) walk(cat.children);
    });
  };
  walk(tree);
  return map;
}

// ==================== Provider ====================

export function CategoryCacheProvider({ children }) {
  const [categories, setCategories] = useState(() => readCache() || []);
  const [loaded, setLoaded] = useState(() => !!readCache());
  const [loading, setLoading] = useState(false);
  const fetchRef = useRef(null); // dedup concurrent calls

  // Derived: flat map + L1 categories
  const categoryMap = useMemo(() => buildCategoryMap(categories), [categories]);
  const level1Categories = useMemo(() => categories, [categories]); // root nodes = L1

  // Fetch from API (deduplicates concurrent calls)
  const fetchFromApi = useCallback(async () => {
    // If a fetch is already in progress, return the same promise
    if (fetchRef.current) return fetchRef.current;

    const promise = (async () => {
      setLoading(true);
      try {
        const res = await encryptedPost("/categories/list", {});
        // Require a NON-EMPTY array. An empty payload must not mark the cache
        // "loaded" nor get persisted — otherwise the dropdown stays empty and
        // no further retry ever fires.
        if (res?.errCode === 0 && Array.isArray(res.data) && res.data.length > 0) {
          setCategories(res.data);
          setLoaded(true);
          writeCache(res.data);
          return res.data;
        }
        return categories;
      } catch {
        return categories;
      } finally {
        setLoading(false);
        fetchRef.current = null;
      }
    })();

    fetchRef.current = promise;
    return promise;
  }, [categories]);

  // Prefetch: use cache if available, else fetch
  const prefetch = useCallback(async () => {
    const cached = readCache();
    if (cached && cached.length > 0) {
      setCategories(cached);
      setLoaded(true);
      return cached;
    }
    return fetchFromApi();
  }, [fetchFromApi]);

  // Invalidate: clear cache + refetch (after category CRUD)
  const invalidate = useCallback(async () => {
    clearCache();
    return fetchFromApi();
  }, [fetchFromApi]);

  // Auto-prefetch on mount (runs once when Layout mounts after login).
  // Uses the shared /boot/master-data endpoint; falls back to /categories/list
  // if boot returns null for categories (user lacks permission) or fails.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cached = readCache();
      if (cached && cached.length > 0) {
        setCategories(cached);
        setLoaded(true);
        return;
      }
      const boot = await fetchBootMasterData();
      if (cancelled) return;

      if (Array.isArray(boot?.categories) && boot.categories.length > 0) {
        setCategories(boot.categories);
        setLoaded(true);
        writeCache(boot.categories);
      } else {
        // Boot returned null (no permission) OR an EMPTY array (transient blip).
        // An empty array previously passed the bare Array.isArray check, was
        // cached as "loaded", and the /categories/list fallback never fired —
        // leaving the Product Category filter permanently empty. Falling back on
        // empty-too lets the direct endpoint recover the tree.
        fetchFromApi();
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-tab sync: when another tab updates categoryCache, update our state
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === CACHE_KEY && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (parsed?.data && Array.isArray(parsed.data)) {
            setCategories(parsed.data);
            setLoaded(true);
          }
        } catch { /* ignore parse errors */ }
      }
      // Another tab cleared cache (logout)
      if (e.key === CACHE_KEY && !e.newValue) {
        setCategories([]);
        setLoaded(false);
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  // Clear cache on logout (detect token removal)
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "token" && !e.newValue) {
        clearCache();
        setCategories([]);
        setLoaded(false);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const value = useMemo(() => ({
    categories,       // full tree (Array)
    categoryMap,      // flat { [id]: category } lookup
    level1Categories, // root nodes for L1 filter dropdowns
    loaded,           // true after first successful load
    loading,          // true during fetch
    prefetch,         // trigger load (idempotent, uses cache)
    invalidate,       // clear cache + refetch (after CRUD)
  }), [categories, categoryMap, level1Categories, loaded, loading, prefetch, invalidate]);

  return (
    <CategoryCacheContext.Provider value={value}>
      {children}
    </CategoryCacheContext.Provider>
  );
}

/**
 * Hook to access the shared category cache.
 * Returns: { categories, categoryMap, level1Categories, loaded, loading, prefetch, invalidate }
 */
export function useCategoryCache() {
  const ctx = useContext(CategoryCacheContext);
  if (!ctx) {
    throw new Error("useCategoryCache must be used within a CategoryCacheProvider");
  }
  return ctx;
}
